import { BaseMutations } from '@/store/base-mutations';
import { Cart } from '@/modules/cart/types';
import { SharedCartOrganization } from '@/modules/cart/shared/types';
import { PaymentMethods } from '@/enums';
import { Orders } from '../types';
import OrderState from './state';

export default class OrderMutations extends BaseMutations<OrderState> {
  SET_FILTERS(filters: Orders.TableFilters) {
    this.state.organizations = filters.organizations;
    this.state.external_organizations = filters.external_organizations;
    this.state.users = filters.users;
    this.state.categories = filters.categories;
    this.state.contracts = filters.contracts;
    this.state.statuses = filters.statuses;
    this.state.shipment_statuses = filters.shipment_statuses;
    this.state.payment_types = filters.payment_types;
  }

  SET_LOADING(value: boolean) {
    this.state.loading = value;
  }

  SET_ADDITIONAL(additional: Orders.Additional) {
    this.state.additional = additional;
  }

  SET_NEW_ORDER(order: Orders.Order) {
    this.state.new_order = order;
  }

  UPD_NEW_ORDER_FIELD({ key, value }: { key: keyof Orders.Order; value: unknown }) {
    this.state.new_order[key] = value;
  }

  SET_ORDER_PRODUCTS_LIST(products_list: Cart.CartItem[]) {
    this.state.new_order.products = products_list;
  }

  UPD_ORDER_PRODUCT(items: Orders.UpdateOrderDTO | Orders.UpdateOrderDTO[]) {
    const get_idx = (id: number | string) =>
      this.state.new_order.products.findIndex((e) => e.id === id);

    if (Array.isArray(items)) {
      items.forEach((item) => {
        const i = get_idx(item.id);

        if (i >= 0) {
          this.state.new_order.products[i][item.key] = item.value;
        }
      });
    } else {
      const i = get_idx(items.id);

      if (i >= 0) {
        this.state.new_order.products[i][items.key] = items.value;
      }
    }
  }

  SET_CREATED_ORDER(new_order: Orders.CreateOrderResponse) {
    this.state.created_order = new_order;
  }

  SET_ORDER_ORGANIZATION(orgs: SharedCartOrganization[]) {
    this.state.order_organizations = orgs;
  }

  UPD_FILTER_KEY({ key, value }: { key: keyof Orders.RequestFilters | string; value: unknown }) {
    this.state.filters[key] = value;
  }

  SET_ORDERS(orders: Orders.Item[]) {
    this.state.items = orders;
  }

  SET_ORDER_LOADING(value: boolean) {
    this.state.order_loading = value;
  }

  SET_ORDER_DETAILS(order: Orders.OrderDetailedInfo) {
    this.state.order_details = order;
  }

  SET_ORDER_DOCUMENTS(documents_list: Orders.OrderDocument[]) {
    this.state.order_documents = documents_list;
  }

  SET_ORDER_HISTORY_LOGS(history_logs: Orders.OrderHistoryLog[]) {
    this.state.order_history_logs = history_logs;
  }

  SET_LAST_ORDER_INFO(last_order_info: Orders.LastCardType) {
    this.state.last_order_info = { ...last_order_info };
  }

  //   update products list
  UPD_ORDER_DETAILS(items: Orders.OrderProductInfo[]) {
    this.state.order_details.products = items;
  }

  UPDATE_NOTIFICATION_LOADING(value: boolean) {
    this.state.order_button_loading = value;
  }

  SET_CANCEL_REASONS(cancel_reason: Orders.OrderCancelReasons[]) {
    this.state.cancel_reasons = cancel_reason;
  }

  UPD_CANCEL_REASON_DIALOG({ key, value }: { key: keyof Orders.CancelResonDialog, value: unknown }) {
    this.state.cancel_order_dialog[key] = value;
  }

  CLEAR_CANCEL_REASON_DIALOG() {
    this.state.cancel_order_dialog.visible = false;
    this.state.cancel_order_dialog.cancel_reason_id = 0;
  }

  SET_CART_LOADED(value: boolean) {
    this.state.is_cart_loaded = value;
  }

  SET_SHIPMENT_INFORMATION(value: Orders.OrderShipment[]) {
    this.state.shipment_orders_information = value;
  }

  SET_SHIPMENTS_ORDERS_DATE(value: Orders.OrderItem[]) {
    function shipment_orders_update(shipment_orders_date: Orders.OrderItem[], new_shipment_orders_date: Orders.OrderItem[]) {
      new_shipment_orders_date.forEach((new_item) => {
        const idx = shipment_orders_date.findIndex((item) => item.id === new_item.id);

        if (idx !== -1) {
          shipment_orders_date[idx] = new_item;
        } else {
          shipment_orders_date.push(new_item);
        }
      });
    }
    shipment_orders_update(this.state.shipment_orders_date, value);
  }

  CLEAR_SHIPMENT_ORDERS(ids: number[]) {
    this.state.shipment_orders_date = this.state.shipment_orders_date.filter((item) => !ids.includes(item.id));
  }

  CLEAR_SHIPMENTS_ORDERS_DATE() {
    this.state.shipment_orders_date = [];
  }

  SET_SHIPMENT_CONFIRM_DIALOG(value: boolean) {
    this.state.shipment_confirm_dialog = value;
  }

  SET_DATE_SEND_SUCCESS_MODAL(value: boolean) {
    this.state.date_sent_success_modal = value;
  }

  TOGGLE_IS_CLEAR(value: boolean) {
    this.state.is_clear_field = value;
  }

  UPD_NEW_ORDER_PAYMENT_TYPE(value: PaymentMethods) {
    this.state.new_order.payment_type = value;
  }

  CLEAR_ORDER_DOCUMENTS() {
    this.state.order_documents = [];
  }

  SET_DELIVERY_DATE(dates: string) {
    this.state.delivery_date = dates;
  }

  CLEAR_DELIVERY_DATE() {
    this.state.delivery_date = '';
  }

  UPD_ALL_SELECTED_ORDERS(value: boolean) {
    this.state.items = this.state.items.map((item: Orders.Item) => ({
      ...item,
      selected: value,
    }));
  }

  UPD_SELECTED_ORDERS({ item, value }: { item: Orders.Item, value: boolean }) {
    if (!item) {
      return;
    }

    this.state.items = this.state.items.map((order) =>
      (order.id === item.id ? { ...order, selected: value } : order));
  }

  SET_SHIPMENT_CONFIRM_ERROR(value: boolean) {
    this.state.shipment_confirm_error = value;
  }

  SET_SHIPMENT_CONFIRM_ERROR_MESSAGE(value: string) {
    this.state.shipment_confirm_error_message = value;
  }

  UPD_IS_REALISATION_CONTRACT(value: boolean) {
    this.state.new_order.is_realization_contract = value;
  }

  UPD_IS_CASH_REGISTERS_AUTO_ADDITION(value: boolean) {
    this.state.new_order.is_cash_registers_auto_addition = value;
  }
}
